import axios from 'axios';
import { auth } from '../lib/firebase';
import {
  Video,
  VideoUploadResponse,
  AnalysisRequest,
  AnalysisResponse,
  Clip,
  ExportRequest,
  ExportResponse,
  TimelineData,
  ExportFormatInfo,
  StorageStats,
  VideoStatus
} from '../types';
import logger from '../lib/logger';

// User-friendly error message mapping
const getUserFriendlyError = (error: any): string => {
  const status = error.response?.status;
  const message = error.message?.toLowerCase() || '';
  const data = error.response?.data?.detail || '';

  // Network errors
  if (error.code === 'ECONNABORTED' || message.includes('timeout')) {
    return 'Request timed out. Large videos may take several minutes to process. Please wait a moment and try again, or check your video status in the dashboard.';
  }
  if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
    return 'Network connection error. Please check your internet connection and try again.';
  }

  // Server errors with user-friendly translations
  switch (status) {
    case 400:
      if (data.includes('already being processed')) return 'This video is already being processed. Please wait for it to complete.';
      if (data.includes('too large')) return 'File is too large. Please try a smaller video file.';
      if (data.includes('unsupported format')) return 'This video format is not supported. Please try converting to MP4.';
      return data || 'Invalid request. Please check your input and try again.';
    
    case 401:
      return 'Session expired. Please sign in again to continue.';
    
    case 403:
      if (data.includes('Pro subscription')) return 'This feature requires a Pro subscription. Upgrade to unlock it!';
      return 'Access denied. You don\'t have permission for this action.';
    
    case 404:
      return 'Content not found. It may have been deleted or moved.';
    
    case 413:
      return 'File is too large for upload. Please use a smaller video file.';
    
    case 429:
      return 'Too many requests. Please wait a moment before trying again.';
    
    case 500:
      return 'Server error occurred. Our team has been notified. Please try again later.';
    
    case 503:
      return 'Service temporarily unavailable. Please try again in a few minutes.';
    
    default:
      // Extract user-friendly message from backend if available
      if (data && typeof data === 'string' && !data.includes('Exception') && !data.includes('Error:')) {
        return data;
      }
      return 'Something went wrong. Please try again or contact support if the problem persists.';
  }
};

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Increased to 2 minutes for better reliability with video imports
});

// Create a separate instance for upload operations with longer timeout
const uploadApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1800000, // 30 minutes for uploads, exports, and video imports
});

// Create a separate instance for status checks with specific timeout
const statusApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 1 minute for status checks to handle server load
});

// Request interceptor to add authentication token to all instances
const addAuthInterceptor = (instance: any) => {
  instance.interceptors.request.use(
    async (config: any) => {
      const user = auth.currentUser;
      if (user) {
        try {
          const token = await user.getIdToken();
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          logger.error('Failed to get auth token:', error);
        }
      }
      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );
};

// Apply auth interceptor to all instances
addAuthInterceptor(api);
addAuthInterceptor(uploadApi);
addAuthInterceptor(statusApi);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // console.log(`✅ [API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 'No Response';
    
    logger.error(`❌ [API] ${method} ${url} - ${status}:`, {
      message: error.message,
      data: error.response?.data,
      headers: error.response?.headers
    });
    
    // Add user-friendly message to error object
    error.userFriendlyMessage = getUserFriendlyError(error);
    
    return Promise.reject(error);
  }
);

uploadApi.interceptors.response.use(
  (response) => {
    // console.log(`✅ [Upload API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 'No Response';
    
    logger.error(`❌ [Upload API] ${method} ${url} - ${status}:`, {
      message: error.message,
      code: error.code,
      response: error.response?.data
    });
    
    // Add user-friendly message to error object
    error.userFriendlyMessage = getUserFriendlyError(error);
    
    return Promise.reject(error);
  }
);

statusApi.interceptors.response.use(
  (response) => {
    // console.log(`✅ [Status API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 'No Response';
    
    logger.error(`❌ [Status API] ${method} ${url} - ${status}:`, {
      message: error.message,
      code: error.code,
      response: error.response?.data
    });
    
    // Add user-friendly message to error object
    error.userFriendlyMessage = getUserFriendlyError(error);
    
    return Promise.reject(error);
  }
);

export const videoApi = {
  // Upload video (legacy single upload)
  uploadVideo: async (file: File): Promise<VideoUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await uploadApi.post<VideoUploadResponse>('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },

  // Start chunked upload
  startChunkedUpload: async (params: {
    filename: string;
    file_size: number;
    content_type: string;
  }) => {
    const response = await uploadApi.post('/upload/start', null, { params });
    return response.data;
  },

  // Upload chunk (handled by custom hook, not used directly)
  uploadChunk: async (
    uploadId: string,
    chunkData: FormData,
    chunkNumber: number,
    isFinalChunk: boolean
  ) => {
    const response = await uploadApi.post(`/upload/chunk/${uploadId}`, chunkData, {
      params: {
        chunk_number: chunkNumber,
        is_final_chunk: isFinalChunk,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  },

  // Analyze video with intelligent retry for resource constraints
  analyzeVideo: async (request: AnalysisRequest, options?: { silent?: boolean }): Promise<AnalysisResponse> => {
    const maxRetries = 3;
    const retryDelays = [2000, 5000, 10000]; // 2s, 5s, 10s
    let lastError: any = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await api.post<AnalysisResponse>('/analyze', request);
        return response.data;
      } catch (error: any) {
        lastError = error;
        
        // Check if this is a retryable 503 error (resource constraints)
        const is503ResourceError = error.response?.status === 503 && 
          (error.response?.data?.detail?.includes('Server overloaded') ||
           error.response?.data?.detail?.includes('Analysis queue full'));
        
        // Check if this is a 400 error indicating analysis already in progress
        const is400AlreadyProcessing = error.response?.status === 400 && 
          (error.response?.data?.detail?.includes('already being processed') ||
           error.response?.data?.detail?.includes('already in progress'));
        
        // Don't retry on 400 errors (analysis already in progress) - throw immediately
        if (is400AlreadyProcessing) {
          throw error;
        }
        
        // If it's the last attempt or not a retryable error, throw
        if (attempt === maxRetries || !is503ResourceError) {
          throw error;
        }
        
        // For silent mode on retryable errors, log but don't throw until final attempt
        if (options?.silent && is503ResourceError) {
          // console.log(`Silent retry ${attempt + 1}/${maxRetries + 1} for analyze request due to: ${error.response?.data?.detail}`);
        }
        
        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelays[attempt]));
        }
      }
    }
    
    // This should never be reached, but just in case
    throw lastError;
  },

  // Get all videos
  getVideos: async (limit: number = 1000): Promise<Video[]> => {
    const response = await api.get<Video[]>('/videos', {
      params: { limit }
    });
    return response.data;
  },

  // Get exported clips for dashboard
  getExportedClips: async (limit: number = 1000): Promise<Clip[]> => {
    try {
      const response = await api.get('/clips/exported', {
        params: { limit }
      });
      
      // Enhance clips with original video metadata for better display
      const enhancedClips = response.data.map((clip: any) => ({
        ...clip,
        // Use preserved thumbnail or fallback
        thumbnail_url: clip.metadata?.clip_thumbnail_url || clip.thumbnail_url,
        // Show original video title if available
        original_video_title: clip.metadata?.original_video_title || 'Unknown Video',
        // Add permanent flag for UI
        is_permanent: true,
        // Add deletion info if available
        original_video_deleted: clip.metadata?.original_video_deleted || false,
        deletion_date: clip.metadata?.deletion_date
      }));
      
      return enhancedClips;
    } catch (error: any) {
      console.error('Error fetching exported clips:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch exported clips');
    }
  },

  // Get single video
  getVideo: async (videoId: string): Promise<Video> => {
    const response = await api.get<Video>(`/videos/${videoId}`);
    return response.data;
  },

  // Delete video
  deleteVideo: async (videoId: string): Promise<void> => {
    await api.delete(`/videos/${videoId}`);
  },

  // Get video status
  getVideoStatus: async (videoId: string) => {
    const response = await statusApi.get(`/videos/${videoId}/status`);
    return response.data;
  },

  // Get video thumbnail
  getVideoThumbnail: async (videoId: string, timestamp: number = 5): Promise<string> => {
    const response = await api.get(`/videos/${videoId}/thumbnail`, {
      params: { timestamp },
      responseType: 'blob'
    });
    
    // Create object URL from blob
    const imageBlob = new Blob([response.data], { type: 'image/jpeg' });
    return URL.createObjectURL(imageBlob);
  },

  // Get timeline thumbnails for scrubber
  getTimelineThumbnails: async (videoId: string, count: number = 10): Promise<{
    video_id: string;
    duration: number;
    thumbnails: Array<{
      timestamp: number;
      url: string | null;
      index: number;
    }>;
  }> => {
    const response = await api.get(`/videos/${videoId}/timeline-thumbnails`, {
      params: { count }
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/stats');
    return response.data;
  },

  // Cancel video processing
  cancelVideoProcessing: async (videoId: string): Promise<any> => {
    const response = await api.post(`/videos/${videoId}/reset`);
    return response.data;
  },

  // Add this to the videoApi object
  cleanupStuckVideos: () => 
    uploadApi.post('/cleanup-stuck')
      .then(response => response.data),
};

export const clipApi = {
  // Get clips
  getClips: async (videoId?: string, tag?: string, limit = 1000): Promise<Clip[]> => {
    const params = new URLSearchParams();
    if (videoId) params.append('video_id', videoId);
    if (tag) params.append('tag', tag);
    params.append('limit', limit.toString());
    
    const response = await api.get(`/clips?${params}`);
    return response.data;
  },

  // Get single clip
  getClip: async (clipId: string): Promise<Clip> => {
    const response = await api.get(`/clips/${clipId}`);
    return response.data;
  },

  // Update clip
  updateClip: async (
    clipId: string, 
    updates: { title?: string; description?: string; tags?: string[] }
  ): Promise<Clip> => {
    const response = await api.put(`/clips/${clipId}`, updates);
    return response.data.clip;
  },

  // Delete clip
  deleteClip: async (clipId: string): Promise<void> => {
    await api.delete(`/clips/${clipId}`);
  },

  // Get clips for video
  getVideoClips: async (videoId: string): Promise<Clip[]> => {
    const response = await api.get(`/videos/${videoId}/clips`);
    return response.data;
  },

  // Get clip timeline
  getClipTimeline: async (clipId: string): Promise<TimelineData> => {
    const response = await api.get(`/clips/${clipId}/timeline`);
    return response.data;
  },

  // Update clip boundaries (precision timing controls)
  updateClipBoundaries: async (
    clipId: string,
    boundaries: { start_time?: number; end_time?: number }
  ): Promise<{ message: string; clip: Clip; regenerated: boolean; note?: string }> => {
    const response = await api.put(`/clips/${clipId}/boundaries`, boundaries);
    return response.data;
  },

  // Get clip timeline thumbnails for scrubber
  getClipTimelineThumbnails: async (clipId: string, count: number = 12): Promise<{
    clip_id: string;
    duration: number;
    thumbnails: Array<{
      timestamp: number;
      url: string | null;
      index: number;
    }>;
  }> => {
    const response = await api.get(`/clips/${clipId}/timeline-thumbnails`, {
      params: { count }
    });
    return response.data;
  },

  // Generate AI-powered hooks and captions for a clip (Pro feature)
  generateHooksAndCaptions: async (clipId: string): Promise<{
    success: boolean;
    clip_id: string;
    clip_title: string;
    video_name: string;
    content: {
      hooks: string[];
      captions: string[];
      hashtags: string[];
      descriptions: {
        short: string;
        long: string;
      };
      generated_by: string;
    };
  }> => {
    const response = await api.post(`/clips/${clipId}/generate-hooks`);
    return response.data;
  },
};

export const exportApi = {
  // Export clip
  exportClip: async (request: ExportRequest): Promise<ExportResponse> => {
    const response = await uploadApi.post('/export', request);
    return response.data;
  },

  // Get clip exports
  getClipExports: async (clipId: string) => {
    const response = await api.get(`/clips/${clipId}/exports`);
    return response.data;
  },

  // Delete export
  deleteClipExport: async (clipId: string, format: string): Promise<void> => {
    await api.delete(`/clips/${clipId}/exports/${format}`);
  },

  // Get export formats
  getExportFormats: async (): Promise<{ formats: ExportFormatInfo[] }> => {
    const response = await api.get('/export/formats');
    return response.data;
  },

  // Export all formats
  exportAllFormats: async (clipId: string, addWatermark = false, autoCaptions = false) => {
    const response = await uploadApi.post(`/clips/${clipId}/export-all`, {
      add_watermark: addWatermark,
      auto_captions: autoCaptions,
    });
    return response.data;
  },

  // Download original clip
  downloadClip: async (clipId: string): Promise<void> => {
    const response = await api.get(`/clips/${clipId}/download`, {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'clip.mp4';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Download exported clip
  downloadExport: async (clipId: string, format: string): Promise<void> => {
    const response = await api.get(`/clips/${clipId}/exports/${format}/download`, {
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `clip_${format}.mp4`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Preview clip
  previewClip: async (clipId: string) => {
    const response = await api.get(`/clips/${clipId}/preview`);
    return response.data;
  },

  // Preview export
  previewExport: async (clipId: string, format: string) => {
    const response = await api.get(`/clips/${clipId}/exports/${format}/preview`);
    return response.data;
  },
};

export const healthApi = {
  // Health check
  getHealth: async (): Promise<{ status: string; storage_stats: StorageStats }> => {
    const response = await api.get('/health');
    return response.data;
  },
};

export const userApi = {
  getQuota: () => api.get('/api/v1/user/quota'),
  validateUpload: (fileSize: number) => api.get(`/api/v1/user/validate-upload?file_size=${fileSize}`),
  
  // Check if user can upload (returns {allowed: boolean, reason: string})
  canUpload: async (fileSize: number) => {
    const response = await api.post('/user/can-upload', { file_size: fileSize });
    return response.data;
  },
  
  // Trial abuse prevention
  validateSignup: (email: string) => 
    api.post('/api/v1/user/validate-signup', { email }),
  
  getIpUsageStats: () => 
    api.get('/api/v1/user/ip-usage-stats'),
  
  // Complete signup with referral code
  completeSignup: (referralCode?: string) => 
    api.post('/api/v1/user/complete-signup', { referral_code: referralCode || '' })
};

export const referralApi = {
  // Get user's referral code
  getMyCode: () => api.get('/referrals/my-code'),
  
  // Apply a referral code
  applyCode: (referralCode: string) => 
    api.post('/referrals/apply', { referral_code: referralCode }),
  
  // Get referral statistics
  getStats: () => api.get('/referrals/stats'),
  
  // Validate referral code (public endpoint)
  validateCode: async (referralCode: string) => {
    const response = await axios.get(`/api/v1/referrals/validate/${referralCode}`);
    return response.data;
  }
};

export const analyticsApi = {
  // Get public statistics (no auth required)
  getPublicStats: async (): Promise<{ total_videos: number; total_clips: number; total_downloads: number; total_users: number }> => {
    const response = await axios.get(`${API_BASE_URL}/analytics/public-stats`);
    return response.data;
  },
};

// Utility functions
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const formatTimestamp = (timestamp: string): string => {
  try {
    // Parse the ISO string properly - this will automatically convert from UTC to local time
    const date = new Date(timestamp);
    
    // Validate the date
    if (isNaN(date.getTime())) {
      logger.error('Invalid timestamp:', timestamp);
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Return relative time for recent dates
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    // For older dates, show localized date and time
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    logger.error('Error formatting timestamp:', timestamp, error);
    return 'Invalid date';
  }
};

export const getStatusColor = (status: VideoStatus): string => {
  switch (status) {
    case 'uploaded':
      return 'badge-info';
    case 'processing':
      return 'badge-warning';
    case 'analyzed':
      return 'badge-success';
    case 'error':
      return 'badge-error';
    default:
      return 'badge-info';
  }
};

export const getHighlightColor = (type: string): string => {
  switch (type) {
    case 'audio_spike':
      return 'bg-red-500';
    case 'facial_emotion':
      return 'bg-gray-600';
    case 'combined':
      return 'bg-gray-900';
    default:
      return 'bg-gray-500';
  }
};

export default api;

// Export uploadApi for chunked uploads
export { uploadApi }; 